import * as React from 'react';
import './Membership.css';
import FadeIn from '../Animations/FadeIn';
import { Button } from '../Components/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Components/Card';
import { useCart } from '../DataService/CartContext';
import { MembershipPlanService } from '../DataService/MembershipPlanService';
import { useNavigate } from 'react-router-dom';
import { CartItem } from '../Types/CartTypes';
import { MembershipPlanDto } from '../Types/MembershipPlanTypes';

const growthSteps = [
  {
    title: '1. Medvetenhet & stabilisering',
    text: 'Förstå ditt nervsystem, din stress, din energi och dina behov.',
  },
  {
    title: '2. Inre förändring',
    text: 'Utforska mönster, självkänsla, relationer och det som påverkar dina val.',
  },
  {
    title: '3. Tillämpning i vardagen',
    text: 'Omsätt kunskapen i vanor, gränser, relationer och självledarskap.',
  },
  {
    title: '4. Integration & utveckling',
    text: 'Skapa en mer hållbar riktning och ett sätt att leva som fungerar över tid.',
  },
];

const monthlyItems = [
  'Ett tydligt månadstema',
  'Fördjupande kunskap',
  'Workbook & reflektion',
  'En konkret huvudövning',
  'Verktyg för nervsystemsreglering',
  'En integrationsuppgift',
];

const rotQuestions = [
  {
    question: 'När kan jag börja?',
    answer: 'GROW är öppet löpande och du kan börja när det passar dig.',
  },
  {
    question: 'Hur mycket tid behöver jag lägga?',
    answer: 'Det väljer du själv. GROW ska vara ett stöd i din vardag – inte ännu ett krav.',
  },
  {
    question: 'Vad är skillnaden mellan Basic och Premium?',
    answer: [
      'Basic ger dig hela GROW-innehållet att arbeta med på egen hand.',
      'Premium ger dig dessutom ett individuellt samtal varje månad och personlig vägledning.',
    ],
  },
  {
    question: 'Måste jag gå ROT-programmet?',
    answer: 'Nej. GROW är ett komplett medlemskap i sig.',
  },
];

const getPlan = (plans: MembershipPlanDto[], key: 'basic' | 'premium') => plans.find((item) => item.key === key && item.isActive) ?? null;

export const Membership = () => {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const membershipOptionsRef = React.useRef<HTMLElement | null>(null);
  const [membershipPlans, setMembershipPlans] = React.useState<MembershipPlanDto[]>([]);

  React.useEffect(() => {
    let mounted = true;

    const loadPlans = async () => {
      try {
        const plans = await MembershipPlanService.getPublic();
        if (mounted) setMembershipPlans(plans ?? []);
      } catch {
        if (mounted) setMembershipPlans([]);
      }
    };

    void loadPlans();

    return () => {
      mounted = false;
    };
  }, []);

  const basicPlan = getPlan(membershipPlans, 'basic');
  const premiumPlan = getPlan(membershipPlans, 'premium');

  const addMembershipToCart = (id: string, title: string, price: number, description: string, plan: string, stripePriceId: string) => {
    const item: CartItem = {
      id,
      type: 'membership',
      referenceId: plan,
      title,
      description,
      price,
      quantity: 1,
      metadata: {
        plan,
        stripePriceId,
        pricingSnapshotJson: JSON.stringify({ planKey: plan, title, description, monthlyPrice: price, currency: 'sek', capturedAtUtc: new Date().toISOString() })
      }
    };

    addItem(item);
    navigate('/cart');
  };

  const scrollToMembershipOptions = () => {
    membershipOptionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="membership-page">
      <section className="section-style">
        <div className="section-one-container membership-hero-grid">
          <div className="membership-hero-copy">
            <FadeIn>
              <p className="membership-kicker">GROW</p>
              <h1 className="membership-hero-title">Väx i din egen takt. Skapa förändring som håller.</h1>
              <div className="membership-section-lead membership-hero-text">
                <p>Du behöver inte börja om varje måndag.</p>
                <p>GROW är för dig som vill må bättre, förstå dig själv och skapa förändringar som fungerar, även när livet inte är perfekt.</p>
                <p>Varje månad får du ett nytt fokus, kunskap, reflektion och konkreta verktyg som hjälper dig att steg för steg skapa mer balans, energi och hållbarhet i vardagen.</p>
                <p>Det handlar inte om att göra mer. Det handlar om att förstå bättre.</p>
              </div>
            </FadeIn>
            <FadeIn>
              <div className="membership-hero-actions">
                <Button text="Jag vill börja med GROW" className="submit-button" style={{ width: 260, cursor: 'pointer' }} onClick={scrollToMembershipOptions} />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="membership-copy-section">
        <div className="section-one-container membership-copy-container">
          <FadeIn>
            <h2>Känner du igen dig?</h2>
            <div className="membership-copy-stack">
              <p>Du vet egentligen vad du "borde" göra, men ändå är det svårt att få förändringen att hålla.</p>
              <p>Kanske går du ofta på högvarv, ställer höga krav på dig själv, hamnar i samma mönster eller längtar efter mer energi och balans.</p>
              <p>Kanske har du provat mycket redan – men tröttnat på att hela tiden börja om.</p>
              <p>Det handlar inte alltid om brist på disciplin.</p>
              <p>Ibland behöver du istället förstå vad som händer, varför du gör som du gör och vad du faktiskt behöver.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="membership-copy-section membership-copy-section-muted">
        <div className="section-one-container membership-copy-container">
          <FadeIn>
            <h2>GROW – en utvecklingsresa, månad för månad</h2>
            <div className="membership-copy-stack">
              <p>GROW är inte ännu en kurs som du ska stressa igenom.</p>
              <p>Det är ett medlemskap som växer med dig.</p>
              <p>Varje månad får du ett nytt fokus att utforska och möjlighet att omsätta det du lär dig i ditt eget liv.</p>
            </div>
          </FadeIn>

          <div className="membership-step-grid">
            {growthSteps.map((step) => (
              <FadeIn key={step.title}>
                <Card className="membership-step-card">
                  <CardHeader>
                    <CardTitle>{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>{step.text}</p>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="membership-copy-section">
        <div className="section-one-container membership-copy-container">
          <FadeIn>
            <h2>Varje månad får du</h2>
          </FadeIn>

          <div className="membership-bullet-grid">
            {monthlyItems.map((item) => (
              <FadeIn key={item}>
                <Card className="membership-bullet-card">
                  <CardContent>
                    <p>{item}</p>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <div className="membership-copy-stack membership-copy-stack-center">
              <p>Du bestämmer själv hur mycket tid du vill lägga.</p>
              <p>Det viktiga är inte att göra allt. Det viktiga är att något börjar förändras i vardagen.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="membership-copy-section membership-copy-section-muted">
        <div className="section-one-container membership-copy-container">
          <FadeIn>
            <h2>GROW växer med dig</h2>
            <div className="membership-copy-stack">
              <p>Du behöver inte hinna allt direkt.</p>
              <p>När du stannar kvar får du möjlighet att bygga vidare på det du redan har lärt dig, återvända till tidigare material och fördjupa sådant du behöver just nu.</p>
              <p>Efter några månader har du börjat förstå dig själv och dina mönster bättre.</p>
              <p>Efter ett halvår har du fler verktyg för stress, gränser, självkänsla och självledarskap.</p>
              <p>Efter ett år har du gått igenom en hel utvecklingsresa – från ökad medvetenhet till större trygghet, självledarskap och hållbarhet.</p>
              <p>Det är inte en nystart varje månad. Det är en resa som bygger vidare.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section id="membership-options" ref={membershipOptionsRef} className="section-five">
        <div className="section-five-container">
          <FadeIn>
            <h2 className="section-five-custom-heading">Välj det stöd som passar dig</h2>
          </FadeIn>

          <div className="section-five-custom-grid">
            <div className="membership-plan-card-shell">
              <Card className="section-five-custom-box">
                <CardHeader className="section-five-card-custom-header">
                  <div className="section-five-card-image-wrap" aria-hidden="true">
                    <img src="/images/grow_leaf_soil.jpg" alt="" className="section-five-card-image" />
                  </div>
                  <CardTitle className="section-five-card-custom-header-text-color">GROW Basic</CardTitle>
                  {basicPlan?.monthlyPrice != null && <CardDescription className="section-five-card-custom-subheading">{basicPlan.monthlyPrice} kr/månad</CardDescription>}
                </CardHeader>
                <CardContent className="section-five-card-content-custom-text-center">
                  <p className="section-five-card-content-custom-margin-bottom">För dig som vill växa i din egen takt.</p>
                  <p>Du får hela GROW-innehållet och en tydlig struktur att följa, utan krav på samtal eller fasta tider.</p>
                  <p>Du får:</p>
                  <ul className="membership-plan-list">
                    <li>Allt månadens material</li>
                    <li>Workbook & reflektion</li>
                    <li>Praktiska övningar</li>
                    <li>Nervsystemsreglering</li>
                    <li>Integrationsuppgifter</li>
                    <li>Tillgång till GROW-biblioteket så länge du är medlem</li>
                  </ul>
                  {basicPlan?.monthlyPrice != null && basicPlan.stripePriceId && (
                    <Button
                      style={{ cursor: 'pointer' }}
                      className="submit-button"
                      text="Jag vill börja med GROW Basic"
                      onClick={() => addMembershipToCart('membership-basic', basicPlan.title, basicPlan.monthlyPrice, basicPlan.description, 'basic', basicPlan.stripePriceId ?? '')}
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="membership-plan-card-shell">
              <Card className="section-five-custom-box">
                <CardHeader className="section-five-card-custom-header">
                  <div className="section-five-card-image-wrap" aria-hidden="true">
                    <img src="/images/grow_lake_pier.jpg" alt="" className="section-five-card-image" />
                  </div>
                  <CardTitle className="section-five-card-custom-header-text-color">GROW Premium</CardTitle>
                  {premiumPlan?.monthlyPrice != null && <CardDescription className="section-five-card-custom-subheading">{premiumPlan.monthlyPrice} kr/månad</CardDescription>}
                </CardHeader>
                <CardContent className="section-five-card-content-custom-text-center">
                  <p className="section-five-card-content-custom-margin-bottom">För dig som vill ha personlig vägledning längs vägen.</p>
                  <p>Ibland räcker det inte att veta vad man behöver göra. Man behöver någon som hjälper en att se klart, sortera och hitta nästa steg.</p>
                  <p>Du får allt i Basic plus:</p>
                  <ul className="membership-plan-list">
                    <li>Ett personligt 1:1-samtal varje månad</li>
                    <li>Individuell guidning</li>
                    <li>Möjlighet att koppla månadens tema till din egen situation</li>
                  </ul>
                  <p>Du får allt i Basic plus ett individuellt samtal varje månad där vi kopplar GROW:s innehåll till din situation.</p>
                  <p>Du får ett tryggt rum för reflektion och utveckling. Hjälp att sortera, se dina mönster och hitta nästa steg.</p>
                  {premiumPlan?.monthlyPrice != null && premiumPlan.stripePriceId && (
                    <Button
                      style={{ cursor: 'pointer' }}
                      className="submit-button"
                      text="Jag vill ha GROW Premium"
                      onClick={() => addMembershipToCart('membership-premium', premiumPlan.title, premiumPlan.monthlyPrice, premiumPlan.description, 'premium', premiumPlan.stripePriceId ?? '')}
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="membership-copy-section membership-copy-section-muted">
        <div className="section-one-container membership-copy-container">
          <FadeIn>
            <h2>ROT-modellen</h2>
            <p className="membership-copy-subtitle">Grunden i mitt sätt att arbeta</p>
            <div className="membership-copy-stack">
              <p>GROW bygger på ROT-modellen, som också är grunden i mina kurser och samtal.</p>
              <p>ROT hjälper oss att se bakom det som syns på ytan och förstå sambanden mellan exempelvis nervsystem, stress, självkänsla, mönster och relationen till dig själv.</p>
              <p>Du behöver inte gå ROT-programmet för att vara med i GROW.</p>
              <p>GROW står helt på egna ben.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="membership-copy-section">
        <div className="section-one-container membership-copy-container">
          <FadeIn>
            <h2>Vill du gå ännu djupare?</h2>
            <div className="membership-copy-stack">
              <p>En till två gånger per år öppnar jag upp ett begränsat antal platser till ROT-programmet.</p>
              <p>Det är en 12-veckors fördjupande kurs för dig som vill arbeta mer intensivt och strukturerat med dina mönster och din förändringsprocess.</p>
            </div>
            <div className="membership-hero-actions">
              <Button text="Läs mer om ROT-programmet" className="submit-button" style={{ width: 260, cursor: 'pointer' }} onClick={() => navigate('/rot/signup')} />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="membership-copy-section membership-copy-section-muted">
        <div className="section-one-container membership-copy-container">
          <FadeIn>
            <h2>Vanliga frågor</h2>
          </FadeIn>

          <div className="membership-faq-grid">
            {rotQuestions.map((item) => (
              <FadeIn key={item.question}>
                <Card className="membership-faq-card">
                  <CardHeader>
                    <CardTitle>{item.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Array.isArray(item.answer) ? item.answer.map((paragraph) => <p key={paragraph}>{paragraph}</p>) : <p>{item.answer}</p>}
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="membership-copy-section">
        <div className="section-one-container membership-copy-container membership-final-cta">
          <FadeIn>
            <div className="membership-copy-stack">
              <p>Du behöver inte börja om.</p>
              <p>Du behöver inte göra mer.</p>
              <p>Du behöver inte kämpa hårdare.</p>
              <p>Du kan börja med att förstå dig själv lite bättre.</p>
              <p>Och därifrån ta nästa steg.</p>
              <p>Välkommen till GROW.</p>
            </div>
            <div className="membership-hero-actions">
              <Button text="Jag vill börja med GROW" className="submit-button" style={{ width: 260, cursor: 'pointer' }} onClick={scrollToMembershipOptions} />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};
